import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { InviteLinkError } from '@/constants';
import type { InviteLinkPreview } from '@/types/inviteLink';
import { getInviterDisplayName } from '@/utils/inviteLink';
import styles from './styles.module.scss';

export type InviteLinkJoinStatus = 'loading' | 'ready' | 'error';
/** Link error code, or a non-link failure ('generic', 'tooManyRequests'). */
export type InviteLinkJoinErrorReason = InviteLinkError | 'generic' | 'tooManyRequests';

export interface InviteLinkJoinAnonymousActions {
  signUpHref: string;
  logInHref: string;
  /** Mini App deep link; null hides the "Open in Telegram" button. */
  telegramAppUrl: string | null;
  /** Inside the Telegram WebApp: silent login with the WebApp initData. */
  onTelegramLogin: (() => void) | null;
  isTelegramLoginPending: boolean;
}

interface InviteLinkJoinProps {
  status: InviteLinkJoinStatus;
  preview: InviteLinkPreview | null;
  /** Error to show when `status === 'error'`. */
  errorReason: InviteLinkJoinErrorReason | null;
  /** Display name of the signed-in user; null for anonymous visitors. */
  viewerName: string | null;
  isJoining: boolean;
  onJoin: () => void;
  anonymousActions: InviteLinkJoinAnonymousActions;
  onGoHome: () => void;
}

const TournamentSummary: React.FC<{ preview: InviteLinkPreview }> = ({ preview }) => {
  const { t } = useTranslation('tournament');
  const { tournament } = preview;
  if (!tournament) return null;

  const inviterName = getInviterDisplayName(preview.inviter);

  const leagueLine = [tournament.leagueName, tournament.season].filter(Boolean).join(' · ');

  return (
    <div className={styles.summary} data-testid="join-link-tournament">
      <div className={styles.summaryHead}>
        {tournament.leagueLogo && (
          <img
            className={styles.logo}
            src={tournament.leagueLogo}
            alt={tournament.leagueName ?? ''}
            width={48}
            height={48}
            decoding="async"
          />
        )}
        <div className={styles.summaryText}>
          <h2 className={styles.tournamentName} data-testid="join-link-tournament-name">{tournament.name}</h2>
          {leagueLine && <p className={styles.league}>{leagueLine}</p>}
        </div>
      </div>
      <dl className={styles.meta}>
        <div className={styles.metaRow}>
          <dt>
            <i className="icon-users" aria-hidden="true" />
            {t('inviteLink.join.participants')}
          </dt>
          <dd data-testid="join-link-participants">
            {tournament.maxParticipants === null
              ? tournament.participantCount
              : t('inviteLink.join.participantsValue', {
                count: tournament.participantCount,
                max: tournament.maxParticipants,
              })}
          </dd>
        </div>
        {inviterName && (
          <div className={styles.metaRow}>
            <dt>
              <i className="icon-user" aria-hidden="true" />
              {t('inviteLink.join.invitedBy')}
            </dt>
            <dd data-testid="join-link-inviter">{inviterName}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

/**
 * Card for `/join/:token`: tournament preview, then either an error state, the explicit
 * "Join" confirmation (signed in) or sign-up / log-in / Telegram CTAs (anonymous).
 */
export const InviteLinkJoin: React.FC<InviteLinkJoinProps> = ({
  status,
  preview,
  errorReason,
  viewerName,
  isJoining,
  onJoin,
  anonymousActions,
  onGoHome,
}) => {
  const { t } = useTranslation('tournament');

  return (
    <div className={styles.page}>
      <article className={styles.card} data-testid="join-link-card" data-status={status}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>PClash</span>
          <h1 className={styles.title}>{t('inviteLink.join.title')}</h1>
        </header>

        {status === 'loading' && (
          <div className={styles.loading} role="status" aria-live="polite" data-testid="join-link-loading">
            <span className="spinner spinner--sm" aria-hidden="true" />
            <span>{t('inviteLink.join.loading')}</span>
          </div>
        )}

        {status !== 'loading' && preview && <TournamentSummary preview={preview} />}

        {status === 'error' && (
          <div className={styles.error} role="alert" data-testid="join-link-error" data-reason={errorReason ?? ''}>
            <h2 className={styles.errorTitle}>{t('inviteLink.join.errorTitle')}</h2>
            <p className={styles.errorText}>{t(`inviteLink.errors.${errorReason ?? 'generic'}`)}</p>
            <button type="button" className="btn btn--sm btn--outlined" onClick={onGoHome} data-testid="join-link-go-home">
              {t('inviteLink.join.goHome')}
            </button>
          </div>
        )}

        {status === 'ready' && viewerName !== null && (
          <div className={styles.actions}>
            <p className={styles.note}>{t('inviteLink.join.signedInAs', { name: viewerName })}</p>
            <div className={styles.buttons}>
              <button
                type="button"
                className="btn btn--sm"
                onClick={onJoin}
                disabled={isJoining}
                data-testid="join-link-accept"
              >
                {isJoining ? t('inviteLink.join.joining') : t('inviteLink.join.join')}
              </button>
              <button type="button" className="btn btn--sm btn--outlined" onClick={onGoHome} data-testid="join-link-not-now">
                {t('inviteLink.join.notNow')}
              </button>
            </div>
          </div>
        )}

        {status === 'ready' && viewerName === null && (
          <div className={styles.actions}>
            <p className={styles.note}>{t('inviteLink.join.anonymousHint')}</p>
            <div className={styles.buttons}>
              {anonymousActions.onTelegramLogin && (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={anonymousActions.onTelegramLogin}
                  disabled={anonymousActions.isTelegramLoginPending}
                  data-testid="join-link-telegram-login"
                >
                  {t('inviteLink.join.continueWithTelegram')}
                </button>
              )}
              <Link
                to={anonymousActions.signUpHref}
                className={`btn btn--sm ${anonymousActions.onTelegramLogin ? 'btn--outlined' : ''}`}
                data-testid="join-link-signup"
              >
                {t('inviteLink.join.signUp')}
              </Link>
              <Link to={anonymousActions.logInHref} className="btn btn--sm btn--outlined" data-testid="join-link-login">
                {t('inviteLink.join.logIn')}
              </Link>
              {anonymousActions.telegramAppUrl && (
                <a
                  href={anonymousActions.telegramAppUrl}
                  className="btn btn--sm btn--outlined"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-link-open-telegram"
                >
                  {t('inviteLink.join.openInTelegram')}
                </a>
              )}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default InviteLinkJoin;
